/**
 * EnterprisePurchasePage
 * Shown after quotation generation for institutional purchases > ₹5,00,000.
 *
 * Routes:
 *   /college/enterprise-purchase/:purchaseId
 *
 * State (from navigate) or fetched from API:
 *   quotation: { quotationNumber, collegeName, planName, creditsPurchased,
 *                subtotal, gst, grandTotal, expiryDate, bankDetails, company }
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import CollegeHeader from "./CollegeHeader";
import CollegeHomeSideBar from "./CollegeHomeSideBar";
import {
  Building2,
  FileText,
  Banknote,
  Upload,
  Check,
  AlertCircle,
  RefreshCw,
  Copy,
  Download,
  ChevronRight,
  Clock,
  Shield,
  X,
  Info,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────
const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—";

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
};

// ── FileUploadField ───────────────────────────────────────────
const FileUploadField = ({ label, hint, onFileSelect, file, onRemove }) => {
  const inputRef = useRef(null);

  return (
    <div>
      <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
        {label}
      </label>
      {file ? (
        <div style={{
          display: "flex", alignItems: "center", gap: "10px",
          background: "#F0FDF4", border: "1.5px solid #BBF7D0",
          borderRadius: "10px", padding: "12px 14px",
        }}>
          <FileText style={{ width: "16px", height: "16px", color: "#059669", flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: "13px", color: "#14532D", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {file.name}
          </span>
          <span style={{ fontSize: "11px", color: "#64748B" }}>
            {(file.size / 1024).toFixed(0)} KB
          </span>
          <button
            onClick={onRemove}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: "2px", display: "flex" }}
          >
            <X style={{ width: "14px", height: "14px" }} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          style={{
            border: "2px dashed #CBD5E1", borderRadius: "10px",
            padding: "16px", textAlign: "center",
            cursor: "pointer", transition: "all 150ms",
            background: "#F8FAFC",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#94A3B8"; e.currentTarget.style.background = "#F1F5F9"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.background = "#F8FAFC"; }}
        >
          <Upload style={{ width: "20px", height: "20px", color: "#94A3B8", margin: "0 auto 8px", display: "block" }} />
          <p style={{ fontSize: "13px", color: "#475569", fontWeight: 600, margin: "0 0 4px" }}>
            Click to upload
          </p>
          <p style={{ fontSize: "11px", color: "#94A3B8", margin: 0 }}>{hint}</p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.size > 5 * 1024 * 1024) { alert("File size must be under 5 MB."); return; }
          onFileSelect(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

// ── CopyButton ────────────────────────────────────────────────
const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value || ""); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{ background: "none", border: "none", cursor: "pointer", color: copied ? "#059669" : "#94A3B8", padding: "2px", display: "flex", alignItems: "center", gap: "3px" }}
      title="Copy to clipboard"
    >
      {copied ? <Check style={{ width: "13px", height: "13px" }} /> : <Copy style={{ width: "13px", height: "13px" }} />}
    </button>
  );
};

// ── SummaryRow ────────────────────────────────────────────────
const SummaryRow = ({ label, value, accent, bold, borderTop }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "9px 0",
    borderTop: borderTop ? "2px solid #E2E8F0" : "1px dashed #E2E8F0",
    marginTop: borderTop ? "4px" : 0,
  }}>
    <span style={{ fontSize: "13px", color: "#64748B", fontWeight: bold ? 700 : 500 }}>{label}</span>
    <span style={{ fontSize: bold ? "17px" : "13px", fontWeight: bold ? 800 : 600, color: accent || "#0F172A" }}>
      {value}
    </span>
  </div>
);

// ── BankDetailRow ─────────────────────────────────────────────
const BankRow = ({ label, value }) => (
  <div style={{ 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "flex-start", 
    padding: "10px 0", 
    borderBottom: "1px solid #F1F5F9",
    gap: "12px"
  }}>
    <span style={{ 
      fontSize: "12px", 
      color: "#94A3B8", 
      fontWeight: 600, 
      textTransform: "uppercase", 
      letterSpacing: "0.04em",
      flexShrink: 0,
      marginTop: "2px"
    }}>
      {label}
    </span>
    <div style={{ 
      display: "flex", 
      alignItems: "center", 
      gap: "6px",
      textAlign: "right",
      flexWrap: "wrap",
      justifyContent: "flex-end"
    }}>
      <span style={{ 
        fontSize: "13px", 
        fontWeight: 700, 
        color: "#0F172A", 
        fontFamily: /^\d/.test(value) ? "monospace" : undefined,
        wordBreak: "break-word"
      }}>
        {value}
      </span>
      {value && <CopyButton value={value} />}
    </div>
  </div>
);

const EVOLVEX_LOGO_URL = 'https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/college_uploads/evolvex-logo.jpeg';

// ── PDF Generation (client-side using jsPDF) ──────────────────
/** Load an image URL into a base64 data-URL via fetch (more robust for S3/CORS) */
const fetchImageAsBase64 = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const generateQuotationPDF = async (quotation) => {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, H = 297;
  const margin = 14;
  const contentW = W - margin * 2;
  let y = margin;

  // Fetch EvolveX logo (non-blocking — falls back gracefully if unavailable)
  const logoBase64 = await fetchImageAsBase64(EVOLVEX_LOGO_URL);

  // ── Color palette
  const BRAND   = [79, 70, 229];   // indigo-600

  // ── HEADER BAND ───────────────────────────────────────────────
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, W, 28, 'F');

  // Company logo + name
  let textStartX = margin;
  if (logoBase64) {
    // Logo: 18×18 mm, vertically centred in the 28mm band, left-aligned
    const logoSize = 18;
    const logoY = (28 - logoSize) / 2;
    doc.addImage(logoBase64, 'JPEG', margin, logoY, logoSize, logoSize);
    textStartX = margin + logoSize + 4;
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(quotation.company?.name || 'EvolveX Innovations', textStartX, 11);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Skill Genie — AI-Powered Learning Platform', textStartX, 17);
  doc.text(`GSTIN: ${quotation.company?.gstin || "37BABPP4434R1Z4"}`, textStartX, 22);

  // OFFICIAL QUOTATION title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('OFFICIAL QUOTATION', W - margin, 13, { align: 'right' });

  y = 42;

  // Quotation meta
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Quotation Details", margin, y);
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const metaRows = [
    ["Quotation Number", quotation.quotationNumber || "—"],
    ["Date", new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })],
    ["Valid Until", formatDate(quotation.expiryDate) + " (15 Days)"],
  ];
  metaRows.forEach(([label, val]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label + ":", margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(val, margin + 45, y);
    y += 6;
  });

  y += 4;

  // Bill To
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, contentW, 32, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text("BILL TO", margin + 4, y + 7);
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(11);
  doc.text(quotation.collegeName || "College Name", margin + 4, y + 15);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  if (quotation.collegeAddress) doc.text(quotation.collegeAddress, margin + 4, y + 21);
  if (quotation.collegeGst) doc.text("GSTIN: " + quotation.collegeGst, margin + 4, y + 27);
  y += 38;

  // Items table header
  doc.setFillColor(...BRAND);
  doc.rect(margin, y, contentW, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text("Description", margin + 4, y + 5.5);
  doc.text("Credits", W / 2, y + 5.5, { align: "center" });
  doc.text("Rate / Credit", W - margin - 50, y + 5.5, { align: "center" });
  doc.text("Amount", W - margin - 4, y + 5.5, { align: "right" });
  y += 8;

  // Items row
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, y, contentW, 10, "F");
  doc.setFont("helvetica", "normal");
  doc.setTextColor(15, 23, 42);
  doc.text((quotation.planName || "Credit Plan") + " – Student Credits", margin + 4, y + 6.5);
  doc.text(String(quotation.creditsPurchased || 0), W / 2, y + 6.5, { align: "center" });
  doc.text("Rs. " + (quotation.pricePerCredit || 0).toLocaleString("en-IN"), W - margin - 50, y + 6.5, { align: "center" });
  doc.text("Rs. " + (quotation.subtotal || 0).toLocaleString("en-IN"), W - margin - 4, y + 6.5, { align: "right" });
  y += 14;

  // Totals — right half of page
  const totalsX = W / 2;
  const totalsW = W / 2 - margin;
  const drawTotalRow = (label, value, isBold) => {
    if (isBold) {
      doc.setFillColor(...BRAND);
      doc.rect(totalsX, y, totalsW, 9, "F");
      doc.setTextColor(255, 255, 255);
    } else {
      doc.setTextColor(71, 85, 105);
    }
    doc.setFont("helvetica", isBold ? "bold" : "normal");
    doc.setFontSize(isBold ? 10 : 9);
    doc.text(label, totalsX + 4, y + 6);
    doc.text(value, totalsX + totalsW - 4, y + 6, { align: "right" });
    y += isBold ? 9 : 7;
  };
  drawTotalRow("Subtotal (excl. GST)", "Rs. " + (quotation.subtotal || 0).toLocaleString("en-IN"));
  drawTotalRow("GST @ 18%",            "Rs. " + (quotation.gst     || 0).toLocaleString("en-IN"));
  drawTotalRow("GRAND TOTAL",          "Rs. " + (quotation.grandTotal || 0).toLocaleString("en-IN"), true);
  y += 8;

  // Payment terms
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Payment Terms", margin, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text("100% Advance via NEFT / RTGS bank transfer.", margin, y);
  y += 10;

  // Bank details
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text("Bank Details", margin, y);
  y += 6;

  const bd = quotation.bankDetails || {};
  const bankRows = [
    ["Account Name",   bd.accountName || "Evolve X Innovations"],
    ["Account Number", bd.accountNumber || "—"],
    ["IFSC Code",      bd.ifsc || "—"],
    ["Bank",           bd.bankName || "—"],
    ["Branch",         bd.branch || "—"],
  ];
  if (bd.upi) bankRows.push(["UPI ID", bd.upi]);

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, contentW, bankRows.length * 7 + 6, 3, 3, "F");
  y += 4;
  bankRows.forEach(([label, val]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text(label + ":", margin + 4, y + 4);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(15, 23, 42);
    doc.text(val, margin + 50, y + 4);
    y += 7;
  });
  y += 6;

  // Terms & Conditions
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text("Terms & Conditions", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  const terms = [
    "1. This quotation is valid for 15 days from the date of issue.",
    "2. Payment to be made 100% in advance via NEFT/RTGS.",
    "3. Credits will be activated within 24 working hours of payment verification.",
    "4. GST @ 18% is applicable and included in the grand total.",
    "5. For queries, contact " + (quotation.company?.email || "connect@evolvexinnovations.org"),
  ];
  terms.forEach((t) => { doc.text(t, margin, y); y += 5; });

  // Footer
  doc.setFillColor(248, 250, 252);
  doc.rect(0, H - 20, W, 20, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text("EvolveX Innovations | The Skill Genie", W / 2, H - 12, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("This is a system-generated quotation.", W / 2, H - 7, { align: "center" });

  doc.save(`Quotation_${quotation.quotationNumber || "TSG"}.pdf`);
};

// ════════════════════════════════════════════════════════════════
// EnterprisePurchasePage
// ════════════════════════════════════════════════════════════════
const EnterprisePurchasePage = () => {
  const { purchaseId } = useParams();
  const { state }      = useLocation();
  const navigate       = useNavigate();

  const [quotation, setQuotation]         = useState(state?.quotation || null);
  const [purchase,  setPurchase]          = useState(null);
  const [auditLog,  setAuditLog]          = useState([]);
  const [loading,   setLoading]           = useState(!state?.quotation);
  const [error,     setError]             = useState(null);

  const [utr,       setUtr]               = useState("");
  const [remarks,   setRemarks]           = useState("");
  const [poFile,    setPoFile]            = useState(null);
  const [proofFile, setProofFile]         = useState(null);
  const [utrError,  setUtrError]          = useState("");
  const [submitting, setSubmitting]       = useState(false);
  const [submitted,  setSubmitted]        = useState(false);
  const [pdfLoading, setPdfLoading]       = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);

  // true when the user arrived here from Transaction History (no quotation in nav state)
  const fromHistory = !state?.quotation;

  const applyPurchaseData = useCallback((purchaseData, auditData, bankDetails) => {
    setPurchase(purchaseData);
    if (auditData) setAuditLog(auditData);
    if (!state?.quotation) {
      setQuotation({
        quotationNumber:  purchaseData.quotation_number,
        collegeName:      purchaseData.college_name,
        planName:         purchaseData.plan_name,
        creditsPurchased: purchaseData.credits_purchased,
        subtotal:         purchaseData.subtotal,
        gst:              purchaseData.gst,
        grandTotal:       purchaseData.grand_total,
        bankDetails:      bankDetails,
      });
    }
    if (["SUBMITTED","RESUBMITTED","UNDER_VERIFICATION","APPROVED"].includes(purchaseData.status)) {
      setSubmitted(true);
    }
    // Pre-populate UTR when in clarification mode so college can see what was previously submitted
    if (purchaseData.status === "CLARIFICATION_REQUESTED" && purchaseData.utr_number) {
      setUtr(purchaseData.utr_number);
    }
  }, [state?.quotation]);

  const fetchPurchaseDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const user = getUser();
      if (!user) { setError("Please log in."); return; }
      const res = await axios.get(
        `${API_BASE_URL}/credits/enterprise/purchases/${purchaseId}?college_id=${user.id}`
      );
      if (res.data.success) {
        applyPurchaseData(res.data.purchase, res.data.audit, res.data.bankDetails);
      } else {
        setError(res.data.error || "Failed to load purchase details.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load purchase details.");
    } finally {
      setLoading(false);
    }
  }, [purchaseId, applyPurchaseData]);

  useEffect(() => {
    if (!state?.quotation) {
      fetchPurchaseDetail();
    } else {
      // Still fetch purchase status and audit when arriving with nav state
      const user = getUser();
      if (user) {
        axios.get(`${API_BASE_URL}/credits/enterprise/purchases/${purchaseId}?college_id=${user.id}`)
          .then(res => {
            if (res.data.success) {
              applyPurchaseData(res.data.purchase, res.data.audit, res.data.bankDetails);
            }
          })
          .catch(() => {});
      }
    }
  }, [purchaseId, state, fetchPurchaseDetail, applyPurchaseData]);

  const handleDownloadQuotation = async () => {
    if (!quotation) return;
    setPdfLoading(true);
    try {
      await generateQuotationPDF(quotation);
      // Mark as downloaded
      const user = getUser();
      if (user) {
        axios.post(`${API_BASE_URL}/credits/enterprise/quotation/downloaded`, {
          quotation_number: quotation.quotationNumber,
          college_id:       user.id,
        }).catch(() => {});
      }
    } catch (err) {
      console.error("[PDF gen] Error:", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const validateAndProceed = () => {
    const utrClean = utr.trim().toUpperCase();
    if (!utrClean) { setUtrError("UTR number is required."); return; }
    if (!/^[A-Z0-9]{8,30}$/.test(utrClean)) {
      setUtrError("Invalid UTR. Must be 8–30 alphanumeric characters (letters and numbers only).");
      return;
    }
    setUtrError("");
    // Show confirmation dialog for resubmission; submit directly for first submission
    if (isClarificationMode) {
      setShowConfirm(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    const utrClean = utr.trim().toUpperCase();
    setSubmitting(true);
    try {
      const user = getUser();
      if (!user) { alert("Please log in."); return; }

      // Encode files to base64
      const [poB64, proofB64] = await Promise.all([
        poFile    ? fileToBase64(poFile)    : Promise.resolve(null),
        proofFile ? fileToBase64(proofFile) : Promise.resolve(null),
      ]);

      const res = await axios.post(`${API_BASE_URL}/credits/enterprise/submit`, {
        college_id:              user.id,
        enterprise_purchase_id:  purchaseId,
        utr_number:              utrClean,
        remarks:                 remarks.trim() || undefined,
        ...(poB64    ? { purchase_order_base64: poB64,    purchase_order_mime: poFile.type,    purchase_order_name: poFile.name }    : {}),
        ...(proofB64 ? { payment_proof_base64:  proofB64, payment_proof_mime:  proofFile.type, payment_proof_name:  proofFile.name } : {}),
      });

      if (res.data.success) {
        setSubmitted(true);
      } else {
        alert(res.data.error || "Failed to submit. Please try again.");
      }
    } catch (err) {
      console.error("[handleSubmit] Error:", err);
      alert(err.response?.data?.error || "Failed to submit payment details. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", flexDirection: "column" }}>
        <CollegeHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <RefreshCw style={{ width: "32px", height: "32px", color: "#7C3AED", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
            <p style={{ fontSize: "14px", color: "#64748B", fontWeight: 500 }}>Loading enterprise purchase details...</p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", flexDirection: "column" }}>
        <CollegeHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ textAlign: "center", maxWidth: "400px" }}>
            <AlertCircle style={{ width: "40px", height: "40px", color: "#DC2626", margin: "0 auto 16px" }} />
            <p style={{ fontSize: "16px", fontWeight: 700, color: "#0F172A", marginBottom: "8px" }}>Something went wrong</p>
            <p style={{ fontSize: "14px", color: "#64748B", marginBottom: "24px" }}>{error}</p>
            <button onClick={() => navigate("/college/purchase-credits")} style={{ padding: "12px 24px", borderRadius: "12px", background: "linear-gradient(135deg,#7C3AED,#4C1D95)", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer" }}>
              Back to Purchase Credits
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── SUCCESS PAGE ───────────────────────────────────────────
  if (submitted) {
    const isClarification = purchase?.status === "CLARIFICATION_REQUESTED";
    return (
      <div className="college-main-layout" style={{ background: "#F8FAFC", fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <CollegeHeader />
        <div className="college-content-wrapper">
          <CollegeHomeSideBar />
          <main className="college-main-content">
            <div style={{ maxWidth: "640px", margin: "64px auto", padding: "0 24px", textAlign: "center" }}>
              <div style={{
                width: "80px", height: "80px", borderRadius: "24px",
                background: "linear-gradient(135deg,#7C3AED,#4C1D95)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 28px",
                boxShadow: "0 8px 32px rgba(124,58,237,0.3)",
              }}>
                <Check style={{ width: "40px", height: "40px", color: "#fff", strokeWidth: 2.5 }} />
              </div>

              <h1 style={{ fontSize: "26px", fontWeight: 900, color: "#0F172A", margin: "0 0 12px", letterSpacing: "-0.02em" }}>
                Payment Request Submitted Successfully
              </h1>
              <p style={{ fontSize: "15px", color: "#64748B", lineHeight: 1.7, margin: "0 0 32px", fontWeight: 500 }}>
                Your payment details have been submitted.<br />
                Our finance team will verify your payment within{" "}
                <strong style={{ color: "#0F172A" }}>24 working hours</strong>.
                You will receive an email and portal notification once approved.
              </p>

              {quotation?.quotationNumber && (
                <div style={{
                  background: "#F5F3FF", border: "1.5px solid #DDD6FE",
                  borderRadius: "16px", padding: "20px 24px", marginBottom: "28px",
                  textAlign: "left",
                }}>
                  <p style={{ fontSize: "11px", fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>
                    Reference Details
                  </p>
                  {[
                    { label: "Quotation Number", value: quotation.quotationNumber },
                    { label: "Credits Requested", value: (quotation.creditsPurchased || 0).toLocaleString("en-IN") + " credits" },
                    { label: "Grand Total", value: formatINR(quotation.grandTotal) },
                    { label: "Status", value: "Pending Verification" },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #EDE9FE" }}>
                      <span style={{ fontSize: "12px", color: "#7C3AED", fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: "13px", color: "#4C1D95", fontWeight: 700 }}>{value}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{
                display: "flex", alignItems: "center", gap: "12px",
                background: "#F0FDF4", border: "1px solid #BBF7D0",
                borderRadius: "12px", padding: "14px 18px", marginBottom: "28px",
                textAlign: "left",
              }}>
                <Clock style={{ width: "20px", height: "20px", color: "#059669", flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: "13px", fontWeight: 700, color: "#14532D", margin: "0 0 2px" }}>What happens next?</p>
                  <p style={{ fontSize: "12px", color: "#15803D", margin: 0, lineHeight: 1.5 }}>
                    Our finance team will verify your UTR against bank records and activate
                    your credits automatically upon confirmation. You will receive both email
                    and portal notifications.
                  </p>
                </div>
              </div>

              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  onClick={() => navigate("/college/transaction-history")}
                  style={{
                    padding: "12px 24px", borderRadius: "12px", border: "none",
                    background: "linear-gradient(135deg,#7C3AED,#4C1D95)", color: "#fff",
                    fontSize: "14px", fontWeight: 700, cursor: "pointer",
                  }}
                >
                  View Transaction History
                </button>
                <button
                  onClick={() => navigate("/college/purchase-credits")}
                  style={{
                    padding: "12px 24px", borderRadius: "12px",
                    border: "1.5px solid #E2E8F0", background: "#fff",
                    fontSize: "14px", fontWeight: 600, color: "#475569", cursor: "pointer",
                  }}
                >
                  Back to Plans
                </button>
              </div>
            </div>
          </main>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── MAIN FORM ──────────────────────────────────────────────
  const isClarificationMode = purchase?.status === "CLARIFICATION_REQUESTED";

  return (
    <div className="college-main-layout" style={{ background: "#F8FAFC", fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <CollegeHeader />
      <div className="college-content-wrapper">
        <CollegeHomeSideBar />
        <main className="college-main-content">
          <div style={{ maxWidth: "900px", margin: "0 auto", padding: "36px 24px 72px" }}>

            {/* ── PAGE HEADER ── */}
            <div style={{ marginBottom: "32px" }}>
              <button
                onClick={() => fromHistory ? navigate("/college/transaction-history") : navigate("/college/purchase-credits")}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "13px", color: "#64748B", fontWeight: 500, padding: "0 0 12px", display: "flex", alignItems: "center", gap: "4px" }}
              >
                ← {fromHistory ? "Back to Transaction History" : "Back to Purchase Credits"}
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "8px" }}>
                <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "rgba(124,58,237,0.10)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Building2 style={{ width: "22px", height: "22px", color: "#7C3AED" }} />
                </div>
                <div>
                  <h1 style={{ fontSize: "26px", fontWeight: 900, color: "#0F172A", margin: 0, letterSpacing: "-0.02em" }}>
                    {isClarificationMode ? "Update & Resubmit Payment" : "Enterprise Purchase"}
                  </h1>
                  <p style={{ fontSize: "13px", color: "#64748B", margin: 0, fontWeight: 500 }}>
                    {isClarificationMode ? "Respond to the finance team's clarification request" : "Secure institutional payment via Bank Transfer"}
                  </p>
                </div>
              </div>

              {/* Clarification banner */}
              {isClarificationMode && purchase?.clarification_message && (() => {
                const clarifyEvent = [...auditLog].reverse().find(a => a.action === "CLARIFICATION_REQUESTED");
                return (
                  <div style={{ marginTop: "16px", borderRadius: "14px", overflow: "hidden", border: "2px solid #FED7AA" }}>
                    <div style={{ background: "linear-gradient(135deg,#EA580C,#C2410C)", padding: "12px 18px", display: "flex", alignItems: "center", gap: "10px" }}>
                      <Info style={{ width: "16px", height: "16px", color: "#fff", flexShrink: 0 }} />
                      <span style={{ fontSize: "13px", fontWeight: 800, color: "#fff" }}>Action Required — Clarification Needed</span>
                    </div>
                    <div style={{ background: "#FFF7ED", padding: "16px 18px" }}>
                      {clarifyEvent && (
                        <div style={{ display: "flex", gap: "24px", marginBottom: "12px", paddingBottom: "10px", borderBottom: "1px dashed #FED7AA" }}>
                          <div>
                            <p style={{ fontSize: "10px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 2px" }}>Requested By</p>
                            <p style={{ fontSize: "13px", fontWeight: 700, color: "#7C2D12", margin: 0 }}>{clarifyEvent.performed_by}</p>
                          </div>
                          <div>
                            <p style={{ fontSize: "10px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 2px" }}>Requested On</p>
                            <p style={{ fontSize: "13px", fontWeight: 700, color: "#7C2D12", margin: 0 }}>
                              {new Date(clarifyEvent.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                        </div>
                      )}
                      <p style={{ fontSize: "11px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 6px" }}>Message from Finance Team</p>
                      <p style={{ fontSize: "13px", color: "#7C2D12", margin: 0, lineHeight: 1.7, fontWeight: 500 }}>
                        {purchase.clarification_message}
                      </p>
                      <p style={{ fontSize: "11px", color: "#92400E", fontWeight: 600, margin: "10px 0 0" }}>
                        Update your UTR, replace payment proof / purchase order if needed, add a response in Remarks, then click "Resubmit for Review".
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", alignItems: "start" }}>

              {/* ── LEFT COLUMN ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                {/* Quotation Summary */}
                {quotation && (
                  <div style={{
                    background: "#FFFFFF", border: "1.5px solid #E2E8F0",
                    borderRadius: "20px", overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
                  }}>
                    <div style={{ height: "4px", background: "linear-gradient(90deg,#7C3AED,#4C1D95)" }} />
                    <div style={{ padding: "22px 24px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                        <div>
                          <p style={{ fontSize: "11px", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                            Quotation Summary
                          </p>
                          <p style={{ fontSize: "15px", fontWeight: 800, color: "#0F172A", margin: 0, letterSpacing: "-0.01em" }}>
                            {quotation.quotationNumber}
                          </p>
                        </div>
                        <button
                          onClick={handleDownloadQuotation}
                          disabled={pdfLoading}
                          style={{
                            display: "flex", alignItems: "center", gap: "6px",
                            padding: "8px 14px", borderRadius: "10px",
                            border: "1.5px solid #DDD6FE", background: "#F5F3FF",
                            fontSize: "12px", fontWeight: 700, color: "#7C3AED",
                            cursor: pdfLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {pdfLoading
                            ? <RefreshCw style={{ width: "12px", height: "12px", animation: "spin 1s linear infinite" }} />
                            : <Download style={{ width: "12px", height: "12px" }} />}
                          {pdfLoading ? "Generating..." : "Download PDF"}
                        </button>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <SummaryRow label="College"          value={quotation.collegeName || "—"} />
                        <SummaryRow label="Plan"             value={quotation.planName || "—"} />
                        <SummaryRow label="Credits"          value={(quotation.creditsPurchased || 0).toLocaleString("en-IN") + " credits"} />
                        <SummaryRow label="Rate / Credit"    value={formatINR(quotation.pricePerCredit || 0)} />
                        <SummaryRow label="Subtotal"         value={formatINR(quotation.subtotal)} />
                        <SummaryRow label="GST @ 18%"        value={"+ " + formatINR(quotation.gst)} />
                        <SummaryRow label="Grand Total"      value={formatINR(quotation.grandTotal)} accent="#7C3AED" bold borderTop />
                      </div>

                      {quotation.expiryDate && (
                        <div style={{
                          display: "flex", alignItems: "center", gap: "6px",
                          marginTop: "14px", padding: "10px 12px",
                          background: "#FFFBEB", border: "1px solid #FDE68A",
                          borderRadius: "10px",
                        }}>
                          <Clock style={{ width: "13px", height: "13px", color: "#D97706", flexShrink: 0 }} />
                          <span style={{ fontSize: "12px", color: "#B45309", fontWeight: 500 }}>
                            Quotation valid until {formatDate(quotation.expiryDate)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Bank Details */}
                {quotation?.bankDetails && (
                  <div style={{
                    background: "#FFFFFF", border: "1.5px solid #E2E8F0",
                    borderRadius: "20px", overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
                  }}>
                    <div style={{ height: "4px", background: "linear-gradient(90deg,#1E3A8A,#4F46E5)" }} />
                    <div style={{ padding: "22px 24px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                        <div style={{ width: "34px", height: "34px", borderRadius: "10px", background: "rgba(79,70,229,0.10)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Banknote style={{ width: "17px", height: "17px", color: "#4F46E5" }} />
                        </div>
                        <div>
                          <p style={{ fontSize: "13px", fontWeight: 800, color: "#0F172A", margin: 0 }}>Enterprise Payment Details</p>
                          <p style={{ fontSize: "11px", color: "#64748B", margin: 0 }}>Transfer funds to the account below</p>
                        </div>
                      </div>

                      <BankRow label="Account Name"   value={quotation.bankDetails.accountName} />
                      <BankRow label="Account Number" value={quotation.bankDetails.accountNumber} />
                      <BankRow label="IFSC Code"      value={quotation.bankDetails.ifsc} />
                      <BankRow label="Bank"           value={quotation.bankDetails.bankName} />
                      <BankRow label="Branch"         value={quotation.bankDetails.branch} />
                      {quotation.bankDetails.upi && (
                        <BankRow label="UPI ID" value={quotation.bankDetails.upi} />
                      )}

                      <div style={{
                        marginTop: "14px", padding: "12px 14px",
                        background: "#EFF6FF", border: "1px solid #BFDBFE",
                        borderRadius: "10px",
                        display: "flex", alignItems: "flex-start", gap: "8px",
                      }}>
                        <Shield style={{ width: "14px", height: "14px", color: "#2563EB", flexShrink: 0, marginTop: "1px" }} />
                        <p style={{ fontSize: "11px", color: "#1E40AF", margin: 0, fontWeight: 500, lineHeight: 1.5 }}>
                          Transfer the exact amount of <strong>{formatINR(quotation.grandTotal)}</strong> (incl. GST).
                          Mention your Quotation Number <strong>{quotation.quotationNumber}</strong> in the payment remarks.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── RIGHT COLUMN: PAYMENT FORM ── */}
              <div style={{
                background: "#FFFFFF", border: "1.5px solid #E2E8F0",
                borderRadius: "20px", overflow: "hidden",
                boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
              }}>
                <div style={{ height: "4px", background: "linear-gradient(90deg,#7C3AED,#1E3A8A)" }} />
                <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: "18px" }}>
                  <div>
                    <p style={{ fontSize: "13px", fontWeight: 800, color: "#0F172A", margin: "0 0 4px" }}>
                      {isClarificationMode ? "Resubmit Payment Details" : "Submit Payment Details"}
                    </p>
                    <p style={{ fontSize: "12px", color: "#64748B", margin: 0 }}>
                      After transferring funds, enter your UTR reference number and upload proof of payment.
                    </p>
                  </div>

                  {/* Purchase Order upload */}
                  <FileUploadField
                    label="Upload Purchase Order (Optional)"
                    hint="PDF, JPG, PNG — max 5 MB"
                    file={poFile}
                    onFileSelect={setPoFile}
                    onRemove={() => setPoFile(null)}
                  />

                  {/* Payment Proof upload */}
                  <FileUploadField
                    label="Upload Payment Proof (Optional)"
                    hint="Bank screenshot, transfer receipt — PDF, JPG, PNG — max 5 MB"
                    file={proofFile}
                    onFileSelect={setProofFile}
                    onRemove={() => setProofFile(null)}
                  />

                  {/* UTR Number (mandatory) */}
                  <div>
                    <label style={{
                      display: "block", fontSize: "12px", fontWeight: 700, color: "#475569",
                      textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px",
                    }}>
                      UTR / Reference Number
                      <span style={{ color: "#DC2626", marginLeft: "4px" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={utr}
                      onChange={(e) => { setUtr(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")); setUtrError(""); }}
                      placeholder="e.g. SBIN024062800001"
                      maxLength={30}
                      style={{
                        width: "100%", padding: "11px 14px",
                        borderRadius: "10px",
                        border: utrError ? "1.5px solid #FCA5A5" : "1.5px solid #E2E8F0",
                        background: "#F8FAFC",
                        fontSize: "14px", fontWeight: 700, color: "#0F172A",
                        fontFamily: "monospace",
                        outline: "none", boxSizing: "border-box",
                        letterSpacing: "0.04em",
                      }}
                    />
                    {utrError ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px" }}>
                        <AlertCircle style={{ width: "13px", height: "13px", color: "#EF4444" }} />
                        <span style={{ fontSize: "12px", color: "#DC2626", fontWeight: 500 }}>{utrError}</span>
                      </div>
                    ) : (
                      <p style={{ fontSize: "11px", color: "#94A3B8", margin: "6px 0 0", fontWeight: 500 }}>
                        The UTR / transaction reference number from your bank statement
                      </p>
                    )}
                  </div>

                  {/* Remarks */}
                  <div>
                    <label style={{
                      display: "block", fontSize: "12px", fontWeight: 700, color: "#475569",
                      textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px",
                    }}>
                      Remarks (Optional)
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Any additional information for the finance team..."
                      rows={3}
                      style={{
                        width: "100%", padding: "11px 14px",
                        borderRadius: "10px", border: "1.5px solid #E2E8F0",
                        background: "#F8FAFC", fontSize: "13px", color: "#0F172A",
                        outline: "none", resize: "vertical",
                        fontFamily: "inherit", boxSizing: "border-box",
                      }}
                    />
                  </div>

                  {/* Status indicator */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    background: "#F8FAFC", borderRadius: "10px", padding: "10px 12px",
                  }}>
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#F59E0B", flexShrink: 0 }} />
                    <span style={{ fontSize: "12px", color: "#92400E", fontWeight: 600 }}>
                      Status: {isClarificationMode ? "Clarification Requested" : "Pending Verification"}
                    </span>
                  </div>

                  {/* Submit button */}
                  <button
                    onClick={validateAndProceed}
                    disabled={submitting}
                    style={{
                      width: "100%", padding: "14px 0",
                      borderRadius: "12px", border: "none",
                      background: submitting ? "#94A3B8" : "linear-gradient(135deg,#7C3AED,#4C1D95)",
                      color: "#FFFFFF", fontSize: "14px", fontWeight: 700,
                      cursor: submitting ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                      boxShadow: submitting ? "none" : "0 4px 14px rgba(124,58,237,0.28)",
                      transition: "opacity 150ms",
                    }}
                    onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.opacity = "0.88"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                  >
                    {submitting ? (
                      <><RefreshCw style={{ width: "15px", height: "15px", animation: "spin 1s linear infinite" }} /> Submitting...</>
                    ) : (
                      <>{isClarificationMode ? "Resubmit for Review" : "Submit Enterprise Payment"} <ChevronRight style={{ width: "15px", height: "15px" }} /></>
                    )}
                  </button>

                  <p style={{ fontSize: "11px", color: "#94A3B8", textAlign: "center", margin: 0, lineHeight: 1.5 }}>
                    By submitting, you confirm that the payment details are accurate.
                    Credits will be activated after verification by our finance team.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── Resubmit Confirmation Dialog ── */}
      {showConfirm && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowConfirm(false); }}
        >
          <div style={{ background: "#fff", borderRadius: "20px", maxWidth: "420px", width: "100%", overflow: "hidden", boxShadow: "0 24px 64px rgba(15,23,42,0.20)" }}>
            <div style={{ background: "linear-gradient(135deg,#EA580C,#C2410C)", padding: "20px 24px" }}>
              <p style={{ fontSize: "16px", fontWeight: 800, color: "#fff", margin: 0 }}>Submit Updated Payment?</p>
              <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", margin: "4px 0 0" }}>This action cannot be undone</p>
            </div>
            <div style={{ padding: "24px" }}>
              <p style={{ fontSize: "14px", color: "#475569", lineHeight: 1.6, margin: "0 0 20px" }}>
                Your updated documents will be sent to the finance team for another review. The status will change to <strong style={{ color: "#0F172A" }}>Pending Verification</strong>.
              </p>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowConfirm(false)}
                  style={{ padding: "10px 20px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#fff", fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{ padding: "10px 22px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg,#EA580C,#C2410C)", fontSize: "13px", fontWeight: 700, color: "#fff", cursor: submitting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px" }}
                >
                  {submitting ? <><RefreshCw style={{ width: "13px", height: "13px", animation: "spin 1s linear infinite" }} /> Submitting...</> : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .ep-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default EnterprisePurchasePage;
